/**
 * In-flight artifact field saves, so an action can wait for them.
 *
 * Editable blocks save on blur, and every artifact action is a separate request
 * that reads the stored artifact. Approving a routine right after editing its
 * "Then do" field therefore races: the blur-save and the approval are both in
 * flight, and the approval's server path is the shorter one, so it usually
 * reads the row before the edit lands and installs the pre-edit prompt
 * (BIG-6261).
 *
 * Module scope rather than React context because the action buttons render as a
 * sibling of `<ArtifactView>`, outside its provider — there is no shared tree to
 * hang this on. Tracking is global rather than per-artifact: the action does not
 * know which artifact it belongs to, and in practice only one card is being
 * edited at a time, so the coupling costs a few milliseconds.
 *
 * That coupling is bounded on purpose. The API client sets no request timeout,
 * so a stalled save never settles — and waiting on it unconditionally would let
 * one hung request lock every artifact action in the app, with the buttons stuck
 * disabled. {@link FLUSH_TIMEOUT_MS} caps the wait: past it the action proceeds
 * anyway, which is no worse than the racing behavior this replaced, and far
 * better than a UI that never recovers.
 */
const inFlight = new Set<Promise<unknown>>();

/**
 * How long an action waits for in-flight saves before going ahead regardless.
 * Long enough that a normal save always wins, short enough that a stalled one
 * doesn't read as a frozen page.
 */
export const FLUSH_TIMEOUT_MS = 5_000;

/**
 * Register a save so {@link flushPendingArtifactEdits} can wait for it. Returns
 * the promise unchanged, so callers can wrap in place.
 */
export const trackArtifactEdit = <T>(promise: Promise<T>): Promise<T> => {
  // Track a derived promise that never rejects. The caller still gets the
  // original, so a failed save rejects for them exactly as before — but nothing
  // here has to handle it, and an unhandled rejection can't escape this module.
  const settled = promise.then(
    () => undefined,
    () => undefined,
  );
  inFlight.add(settled);
  void settled.finally(() => inFlight.delete(settled));
  return promise;
};

/**
 * Commit whatever the card is still holding, then wait for it to land.
 *
 * Blurring first matters as much as the wait: a field the user never left keeps
 * its value in local state, and its blur-save has not started at all. Moving
 * focus off it is what triggers the save this then waits for.
 *
 * Rejections are swallowed — a failed save has already shown the user its own
 * error toast, and the action it was blocking should still be allowed to run
 * rather than being silently dropped here.
 */
export const flushPendingArtifactEdits = async (): Promise<void> => {
  const focused = focusedEditableField();
  if (focused) {
    focused.blur();
    // One macrotask, so the blur handler (and the React state updates it
    // triggers) has run and registered its request before we read the set.
    // Only paid when a field actually held focus — an action fired with
    // nothing being edited resolves without yielding the task queue at all.
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
  if (inFlight.size === 0) return;
  let giveUp: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    Promise.allSettled([...inFlight]),
    new Promise((resolve) => {
      giveUp = setTimeout(resolve, FLUSH_TIMEOUT_MS);
    }),
  ]);
  // Cleared on the normal path so a settled flush doesn't leave a live timer
  // holding the event loop (and, in tests, leaking into the next case).
  if (giveUp !== undefined) clearTimeout(giveUp);
};

/**
 * The focused element, when it is something the user types into — a plain
 * input, or the contenteditable the rich-text blocks render. Anything else
 * holding focus (the action button itself, most obviously) has no edit to
 * commit, so blurring it would achieve nothing.
 */
const focusedEditableField = (): HTMLElement | null => {
  if (typeof document === "undefined") return null;
  const active = document.activeElement;
  if (!(active instanceof HTMLElement)) return null;
  if (active.isContentEditable) return active;
  return active.matches("input, textarea, select") ? active : null;
};

/** Test seam: drop any tracked promises left over from a previous case. */
export const resetPendingArtifactEdits = (): void => {
  inFlight.clear();
};
